import { makeAutoObservable } from "mobx"
import styles from "./styles.module.scss"
import { useEffect, useMemo, useRef } from "react"
import { ref, uploadBytes, getDownloadURL } from "firebase/storage"
import PaperPlaneOutline from "~icons/eva/paper-plane-outline"
import AttachOutline from "~icons/eva/attach-2-outline"
import { observer } from "mobx-react-lite"
import { v4 } from "uuid"
import { filesStorage } from "../../../../../services/firebase"
import { useStores } from "../../../../../models/helpers/useStores"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useFileDragging } from "../../useFileDragging"
import classNames from "classnames"
import { NavigatorFileCard } from "../../../../atoms/NavigatorFileCard"
import TextareaAutosize from "react-textarea-autosize"
import { Tooltip } from "../../../../atoms/Tooltip"
import RobotHeadIcon from "../../../../assets/icons/RobotHeadIcon"

type VisibleTooltip = "attach-file" | "enable-ai" | null

class NavigatorInputStore {
  inputMessage = ""
  savedFile: File | null = null
  visibleTooltip: VisibleTooltip = null

  constructor() {
    makeAutoObservable(this)
  }

  setInputMessage(message: string) {
    this.inputMessage = message
  }

  setSavedFile(value: File | null) {
    this.savedFile = value
  }

  setVisibleTooltip(value: VisibleTooltip) {
    this.visibleTooltip = value
  }

  clearStore() {
    this.inputMessage = ""
    this.savedFile = null
    this.visibleTooltip = null
  }
}

export type NavigatorFile = {
  fileUrl: string
  fileName: string
  thumbnail: string | null
}

export const NavigatorInput = observer(
  ({
    submitForm,
    toggleInputType,
    onTyping,
    inputType,
  }: {
    submitForm: (message: string, fileData: NavigatorFile | null) => Promise<void>
    toggleInputType: () => void
    onTyping: () => void
    inputType: string
  }) => {
    const rootStore = useStores()
    const store = useMemo(() => new NavigatorInputStore(), [])
    const filesUrl = `files/${rootStore.activeTrip.id}/`
    const fileInputRef = useRef<HTMLInputElement | null>(null)
    const formRef = useRef<HTMLFormElement | null>(null)

    const queryClient = useQueryClient()

    const setFile = (file: File | null) => {
      if (!file) return
      const bytesToMb = 1024 * 1024
      if (file.size > 10 * bytesToMb) {
        alert("File size is too large. Max size for navigator file is 10MB.")
        return
      }

      store.setSavedFile(file)
    }

    const uploadFile = async (imageUpload: File) => {
      if (imageUpload == null) return null
      const imageRef = ref(filesStorage, `${filesUrl}${imageUpload.name + v4()}`)

      const metadata = {
        customMetadata: {
          username: `${rootStore.loggedInUser.firstName} ${rootStore.loggedInUser.lastName[0]}.`,
          userId: rootStore.loggedInUser.id,
          fileName: imageUpload.name,
        },
      }

      try {
        const snapshot = await uploadBytes(imageRef, imageUpload, metadata)
        const fileUrl = await getDownloadURL(snapshot.ref)

        console.log("File uploaded", fileUrl)
        queryClient.invalidateQueries({ queryKey: ["navigatorFiles", rootStore.activeTrip.id] })
        store.setSavedFile(null)

        return {
          fileUrl,
          fileName: imageUpload.name,
          thumbnail: imageUpload.type.startsWith("image/") ? fileUrl : null,
        }
      } catch (err) {
        console.error("Uploading file error", err)
        store.setSavedFile(null)
        return null
      }
    }

    const { mutate: onSubmit, isPending } = useMutation({
      mutationFn: async () => {
        let fileData: NavigatorFile | null = null
        if (store.savedFile) {
          fileData = await uploadFile(store.savedFile)
        }
        await submitForm(store.inputMessage, fileData)
        store.setInputMessage("")
      },
    })

    const handleButtonClick = () => {
      if (fileInputRef.current) {
        fileInputRef.current.click()
      }
    }

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.files && event.target.files.length > 0) {
        const selectedFile = event.target.files[0]
        setFile(selectedFile)
      }
    }

    const handlePaste = (event: React.ClipboardEvent) => {
      const items = event.clipboardData.items

      for (let i = 0; i < items.length; i++) {
        if (items[i].kind === "file") {
          const file = items[i].getAsFile()
          if (file) {
            event.preventDefault()
            setFile(file)
            return
          }
        }
      }
    }

    const { isDragging } = useFileDragging({
      uploadFile: (file) => {
        setFile(file)
      },
      isEnabled: rootStore.activeFileDropZone !== "bookingModal",
    })

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault()
        if (store.inputMessage) {
          onSubmit()
        }
      }
    }

    useEffect(() => {
      store.clearStore()
    }, [rootStore.activeTrip.id])

    return (
      <>
        {isDragging && rootStore.activeFileDropZone !== "bookingModal" && (
          <div className={styles.overlay}>
            <h2>Drop file here to upload</h2>
          </div>
        )}
        <form
          ref={formRef}
          onSubmit={(e) => {
            e.preventDefault()
            onSubmit()
          }}
          className={styles.navigatorInputContainer}
          onPaste={handlePaste}
        >
          {store.savedFile ? (
            <NavigatorFileCard
              handleDelete={() => store.setSavedFile(null)}
              file={store.savedFile}
            />
          ) : null}
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={handleFileChange}
            accept=".jpg,.jpeg,.png,.pdf"
          />
          <TextareaAutosize
            placeholder={
              inputType === "navigatorAI" ? "Ask Navigator a question" : "Message your group"
            }
            className={styles.navigatorInput}
            value={store.inputMessage}
            data-testid="navigator-message-input"
            minRows={1}
            maxRows={4}
            onChange={(e) => {
              store.setInputMessage(e.target.value)
              onTyping()
            }}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
          />
          <div className={styles.inputButtons}>
            <div className={styles.navigatorActionButtons}>
              <button
                type="button"
                className={styles.navigatorActionButton}
                onClick={handleButtonClick}
                onMouseEnter={() => store.setVisibleTooltip("attach-file")}
                onMouseLeave={() => store.setVisibleTooltip(null)}
                data-tooltip-id="attach-file"
                data-tooltip-content="Attach file"
              >
                <AttachOutline className={styles.navigatorActionButtonIcon} />
              </button>
              <button
                type="button"
                className={classNames(
                  styles.navigatorActionButton,
                  inputType === "navigatorAI" && styles.navigatorActionButtonActive,
                )}
                onClick={() => toggleInputType()}
                onMouseEnter={() => store.setVisibleTooltip("enable-ai")}
                onMouseLeave={() => store.setVisibleTooltip(null)}
                data-tooltip-id="enable-ai"
                data-tooltip-content="Talk to AI"
              >
                <RobotHeadIcon width={18} height={18} />
              </button>
              <Tooltip
                id={store.visibleTooltip}
                isOpen={store.visibleTooltip !== null}
                place="top"
                variant="dark"
              />
            </div>
            <button
              type="submit"
              className={classNames(
                styles.navigatorButton,
                isPending && styles.navigatorButtonSending,
              )}
              disabled={!store.inputMessage || isPending}
              data-testid="navigator-message-submit"
            >
              <PaperPlaneOutline className={styles.submitIcon} />
            </button>
          </div>
        </form>
      </>
    )
  },
)
